import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import './JewelDetail.css';
import { trackEvent } from '../utils/analytics';
import { formatInrWhole, jewelGstCaption } from '../utils/money';
import { sectionPathForMaterial } from '../utils/sectionPaths';

const MATERIAL_LABELS = {
  gold: 'Gold',
  silver: 'Silver',
  fancy: 'Fancy',
  gold_plated: 'Gold plated',
};

export default function JewelDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jewel, setJewel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const { items, addItem, updateQuantity, removeItem } = useCart();
  const line = jewel ? items.find((i) => i.jewelId === jewel.id) : null;

  useEffect(() => {
    const fetchJewel = async () => {
      try {
        const res = await fetch(`/api/jewels/${id}`);
        if (!res.ok) throw new Error('Jewel not found');
        const data = await res.json();
        setJewel(data);
        setActiveImg(0);
        trackEvent('view_item', {
          item_id: data.id,
          item_name: data.name,
          material: data.material,
          value: data.display_price || 0,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchJewel();
  }, [id]);

  const galleryUrls = useMemo(() => {
    if (!jewel) return [];
    const main = jewel.image_mime_type ? [`/api/jewels/${jewel.id}/image`] : [];
    const extras = (jewel.extra_images || []).map((ex) => `/api/jewel-extra-images/${ex.id}`);
    return [...main, ...extras];
  }, [jewel]);

  const handleAdd = () => {
    addItem(jewel);
    trackEvent('add_to_enquiry', {
      item_id: jewel.id,
      item_name: jewel.name,
      material: jewel.material,
      value: jewel.display_price || 0,
    });
  };

  const gstLine = jewel ? jewelGstCaption(jewel) : null;

  if (loading) {
    return <div className="page-center"><p>Loading...</p></div>;
  }

  if (error || !jewel) {
    return (
      <div className="page-center">
        <div>
          <p className="error-text">{error || 'Jewel not found'}</p>
          <Link to="/" className="back-link">← Back to collection</Link>
        </div>
      </div>
    );
  }

  const materialLabel = MATERIAL_LABELS[jewel.material] || jewel.material;
  const sectionBack = sectionPathForMaterial(jewel.material);

  return (
    <div className="jewel-detail">
      <Link to={sectionBack} className="back-link">
        ← Back to {materialLabel} collection
      </Link>

      <div className="jewel-detail-content">
        <div className="jewel-detail-image">
          {galleryUrls.length > 0 ? (
            <>
              <img src={galleryUrls[activeImg]} alt={jewel.name} className="jewel-detail-main-img" />
              {galleryUrls.length > 1 && (
                <div className="jewel-detail-thumbs">
                  {galleryUrls.map((url, i) => (
                    <button
                      key={url}
                      type="button"
                      className={`jewel-detail-thumb ${i === activeImg ? 'is-active' : ''}`}
                      onClick={() => setActiveImg(i)}
                    >
                      <img src={url} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="jewel-detail-placeholder">No Image Available</div>
          )}
        </div>

        <div className="jewel-detail-info">
          <span className={`detail-badge ${jewel.material === 'gold_plated' ? 'gold-plated' : jewel.material}`}>{materialLabel}</span>
          {jewel.item_number && (
            <p className="jewel-detail-item-no">Item #{jewel.item_number}</p>
          )}
          <h1>{jewel.name}</h1>

          {jewel.display_price != null ? (
            <p className="jewel-detail-price">{formatInrWhole(jewel.display_price)}</p>
          ) : (
            <p className="jewel-detail-price muted">Price unavailable</p>
          )}
          {gstLine && <p className="jewel-detail-gst-line">{gstLine}</p>}

          {jewel.weight_g && jewel.material !== 'fancy' && (
            <p className="jewel-detail-weight">Weight: {jewel.weight_g}g</p>
          )}

          {jewel.price_per_gram != null && (
            <p className="jewel-detail-rate">
              Rate: {formatInrWhole(jewel.price_per_gram)}/g
              {jewel.price_source_ts && (
                <small> (updated {new Date(jewel.price_source_ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })})</small>
              )}
            </p>
          )}

          {jewel.description && (
            <div className="jewel-detail-description">
              <h3>Description</h3>
              <p>{jewel.description}</p>
            </div>
          )}

          {!line ? (
            <button type="button" className="btn btn-primary detail-cart-btn" onClick={handleAdd}>
              + Add to Enquiry
            </button>
          ) : (
            <div className="jewel-detail-enquiry-block">
              <p className="jewel-detail-enquiry-title">In your enquiry cart</p>
              <div className="jewel-detail-qty-row">
                <button
                  type="button"
                  className="jewel-detail-qty-btn"
                  onClick={() => {
                    if (line.quantity <= 1) removeItem(jewel.id);
                    else updateQuantity(jewel.id, line.quantity - 1);
                  }}
                >
                  −
                </button>
                <span className="jewel-detail-qty-val">{line.quantity}</span>
                <button
                  type="button"
                  className="jewel-detail-qty-btn"
                  onClick={() => updateQuantity(jewel.id, line.quantity + 1)}
                >
                  +
                </button>
              </div>
              <button type="button" className="detail-view-cart-btn" onClick={() => navigate('/cart')}>
                View enquiry cart ({items.reduce((s, i) => s + i.quantity, 0)})
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
